// Dashboard.jsx
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from "react";
import TopToolbar from "./TopToolbar";
import EnvironmentalDataControls from "../EnvironmentalDataControls";
import AlertStatusIcons from "../AlertStatusIcons";
import ComparisonPriorityPanel from "../ComparisonPriorityPanel";
import ForecastAlertsTable from "../ForecastAlertsTable";
import SituationStatCircle from "../SituationStatCircle";
import CurrentSituationSummary from "../CurrentSituationSummary";
import SeasonalContextRing from "../SeasonalContextRing";
import MobileSummaryView from "../MobileSummaryView";
import HelpTip from "../HelpTip";
import AboutPanel from "../AboutPanel";
import DashboardTour, {
  markDashboardTourAutoStartAttempted,
  shouldAutoStartDashboardTour,
} from "../DashboardTour";
import { DASHBOARD_HELP } from "../../utils/dashboardHelpText";
import { buildComparisonPriorityCandidates } from "../../utils/buildComparisonPriorityCandidates";
import { buildNeighborSummaryFromTableRows } from "../../utils/buildDeliberationEvidence";
import { getDashboardTourSteps } from "../../utils/dashboardTourSteps";
import { useStudyCondition } from "../../utils/studyCondition";
import DecisionLayers from "../DecisionLayers";
import EnvironmentalLayers from "../EnvironmentalLayers";
import {
  HEALTH_MAP_SURFACE_LAYERS,
  isEnvMapSurfaceLayer,
} from "../MapSurfaceLayerPicker";
import {
  bootstrapCacheKey,
  fetchDistrictForecastDetail,
  fetchEpidemiaCacheStatus,
  fetchLatestEpidemiaReport,
  fetchMapEpidemiaReport,
  formatForecastApiError,
  readBootstrapCache,
  runEpidemiaPipeline,
  waitForPipelineIdle,
} from "../../api";
import {
  buildAdm3Lookup,
  findDistrictFromLookup,
  getDistrictNameVariants,
  normalizeDistrictKey,
  resolveAdminRegionByPcode,
  resolveAdminRegionForDistrict,
} from "../../utils/districtNameMatch";
import { buildAlertTooltipLookup } from "../../utils/buildAlertTooltipLookup";
import {
  buildAlertWeekDates,
  buildAlertsForWeek,
  buildAnimatedAlertTooltipLookup,
  countAlertTypes,
} from "../../utils/buildAlertHistory";
import { buildDistrictTooltipLookup } from "../../utils/buildDistrictTooltipLookup";
import { buildIncidentRateData } from "../../utils/buildIncidentRateData";
import { buildAlertLevelSurface } from "../../utils/buildAlertLevelSurface";
import { buildEpidemiaReportModel } from "../../utils/buildEpidemiaReportModel";
import {
  buildComparisonDistrictOptions,
  buildDistrictForecastSeries,
  buildRegionalComparisonDistricts,
} from "../../utils/buildDistrictForecastSeries";
import {
  buildComparisonHighlightModes,
  FORECAST_VALUE_MODE,
  FORECAST_VALUE_MODE_OPTIONS,
  transformForecastTableRow,
} from "../../utils/forecastValueMode";
import {
  canUseReportScope,
  filterAlertsByAdminRegion,
  filterAlertsByScope,
  REPORT_SCOPE_COUNTRY,
  REPORT_SCOPE_DISTRICT,
  REPORT_SCOPE_REGION,
} from "../../utils/reportScope";
import {
  defaultWoredaPageMode,
  filterWoredaDistrictRows,
  WOREDA_PAGE_MODES,
} from "../../utils/reportExportConfig";
import { speciesToDisease } from "../../utils/projectStorage";
import { collectEpidemiaDataDates, resolveChartDateRange } from "../../utils/chartDateRange";
import {
  normalizeChartAxisDate,
  parseXAxisRangeFromRelayoutEvent,
} from "../../utils/plotlyXAxisSync";
import { useChartPlotRegistry } from "../../utils/useChartPlotRegistry";
import { useMediaQuery } from "../../utils/useMediaQuery";
import { buildSeasonalContext } from "../../utils/buildSeasonalContext";
import { buildPipelineStatus } from "../../utils/buildPipelineStatus";
import { buildDistrictCaseSparkline } from "../../utils/buildDistrictCaseSparkline";
import { enrichAlertsWithForecastWarnings } from "../../utils/enrichAlertWarnings";
import {
  findDistrictForecastRow,
  forecastHistoryCoversRange,
  mergeDistrictForecast,
  mergeForecastBootstrap,
  resolveReportDistrictForSelection,
} from "../../utils/epidemiaReportMerge";
import "./dashboard-theme.css";

const EnvironmentalTimeSeriesChart = lazy(() => import("../EnvironmentalTimeSeriesChart"));
const ForecastChart = lazy(() => import("../ForecastChart"));
const MultiDistrictComparisonChart = lazy(() => import("../MultiDistrictComparisonChart"));
const RegionalAlertSummaryChart = lazy(() => import("../RegionalAlertSummaryChart"));
const DecisionPanel = lazy(() => import("../DecisionPanel"));
const EthiopiaMap = lazy(() => import("../EthiopiaMap"));

/** District choropleth fetch (Earth Engine). Set to true to show the panel again. */
const SHOW_FETCH_ENVIRONMENTAL_DATA_PANEL = false;

const WEATHER_DATASET_OPTIONS = [
  { label: "Precipitation", value: "totprec" },
  { label: "LST Day Temperature", value: "lst_day" },
  { label: "LST Night Temperature", value: "lst_night" },
  { label: "LST Mean Temperature", value: "lst_mean" },
  { label: "NDVI", value: "ndvi" },
  { label: "SAVI", value: "savi" },
  { label: "EVI", value: "evi" },
  { label: "NDWI5", value: "ndwi5" },
  { label: "NDWI6", value: "ndwi6" },
];

function formatPopulation(value) {
  if (value == null || Number.isNaN(Number(value))) return "No population data";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number(value));
}

function DiseaseTitle({ disease, country }) {
  const speciesName = disease.replace(/\s+malaria$/i, "");
  return (
    <>
      <em>{speciesName}</em> malaria Early Warning ({country})
    </>
  );
}

function finiteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function surfaceValueForDistrict(surface, districtName) {
  if (!surface || !districtName) return null;
  const exact = finiteNumber(surface[districtName]);
  if (exact != null) return exact;

  const normalized = finiteNumber(surface[normalizeDistrictKey(districtName)]);
  if (normalized != null) return normalized;

  for (const variant of getDistrictNameVariants(districtName)) {
    const value = finiteNumber(surface[variant] ?? surface[normalizeDistrictKey(variant)]);
    if (value != null) return value;
  }

  return null;
}

function yearFromDate(value) {
  const match = String(value || "").match(/^(\d{4})/);
  return match ? Number(match[1]) : null;
}

function nearestAvailableYear(targetYear, years) {
  if (!targetYear || years.length === 0) return null;
  if (years.includes(targetYear)) return targetYear;

  return years.reduce((nearest, year) => {
    if (nearest == null) return year;
    return Math.abs(year - targetYear) < Math.abs(nearest - targetYear) ? year : nearest;
  }, null);
}

function isObjectRecord(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

async function fetchJsonIfAvailable(url) {
  const res = await fetch(url);
  if (!res.ok) return null;

  const text = await res.text();
  if (!text.trim() || text.trimStart().startsWith("<")) return null;

  return JSON.parse(text);
}

function alertStatus(alert) {
  if (alert?.early_warning) return "Early Warning";
  if (alert?.early_detection) return "Early Detection";
  return "Normal";
}

function runAfterFirstPaint(task) {
  if (typeof window === "undefined") {
    task();
    return () => {};
  }
  if ("requestIdleCallback" in window) {
    const id = window.requestIdleCallback(() => task(), { timeout: 1500 });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(task, 0);
  return () => window.clearTimeout(id);
}

function buildWeekDates(startDate, endDate) {
  const start = Date.parse(`${startDate}T00:00:00Z`);
  const end = Date.parse(`${endDate}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end)) return [];

  const min = Math.min(start, end);
  const max = Math.max(start, end);

  const out = [];
  for (let t = min; t <= max; t += 7 * 24 * 60 * 60 * 1000) {
    out.push(new Date(t).toISOString().slice(0, 10));
  }
  if (out.length === 0) out.push(new Date(max).toISOString().slice(0, 10));
  return out;
}

function Dashboard({
  projectConfig = null,
  bootstrapEpidemiaData = null,
  onBootstrapConsumed,
  onOpenProjectWizard,
  onUseDefaultDataset,
  usingCustomProject = false,
  showDefaultDatasetButton = false,
}) {
  const [disease, setDisease] = useState(
    () => speciesToDisease(projectConfig?.defaultSpecies) || "Plasmodium falciparum malaria"
  );
  const [country, setCountry] = useState("Ethiopia");
  // Default to 12 until a longer cached horizon exists; 20/26 are available after rebuild.
  const [forecastWeeks, setForecastWeeks] = useState(projectConfig?.horizonWeeks || 12);
  const [forecastValueMode, setForecastValueMode] = useState(FORECAST_VALUE_MODE.CASES);
  const [selectedAdminRegion, setSelectedAdminRegion] = useState(
    projectConfig?.defaultRegion || "All Regions"
  );
  /** What the map draws: matches toolbar except a named region is briefly "No Selection" to clear, then the region. */
  const [mapFilterRegion, setMapFilterRegion] = useState(
    projectConfig?.defaultRegion || "All Regions"
  );
  const mapRegionStepTimerRef = useRef(null);
  const initialDistrict = projectConfig?.defaultDistrict || "All Regions";
  const [region, setRegion] = useState(initialDistrict);
  const [selectedGeometry, setSelectedGeometry] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgressMessage, setExportProgressMessage] = useState("");
  const [reportExportConfig, setReportExportConfig] = useState(null);
  const [reportScope, setReportScope] = useState(REPORT_SCOPE_COUNTRY);
  const [woredaPageMode, setWoredaPageMode] = useState(WOREDA_PAGE_MODES.alerts.value);
  const [regions, setRegions] = useState([]); // List of available regions
  const [districts, setDistricts] = useState(["All Regions"]);
  const [epidemiaData, setEpidemiaData] = useState(null);
  const [epidemiaLoading, setEpidemiaLoading] = useState(false);
  const [epidemiaRefreshing, setEpidemiaRefreshing] = useState(false);
  const [epidemiaError, setEpidemiaError] = useState("");
  const [cacheStatus, setCacheStatus] = useState(null);
  const [districtDetailLoading, setDistrictDetailLoading] = useState(false);
  const forecastRequestIdRef = useRef(0);
  const mapRequestIdRef = useRef(0);
  const districtDetailRequestRef = useRef(0);
  const skipInitialForecastLoadRef = useRef(false);
  const onBootstrapConsumedRef = useRef(onBootstrapConsumed);
  onBootstrapConsumedRef.current = onBootstrapConsumed;
  // Keep national map on load unless the project explicitly sets a default district.
  const userPrefersAllDistrictsRef = useRef(initialDistrict === "All Regions");
  const lastAutoExtendedDistrictRef = useRef(null);
  const projectDataDir = projectConfig?.dataDir || "data";
  const projectOutputDir = projectConfig?.outputDir || "report";

  // Chart dates stay empty until the forecast bootstrap finishes — avoids flashing
  // placeholder (1/1/2025→today) then stale bootstrap (→3/23) then final report.
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [forecastBootstrapReady, setForecastBootstrapReady] = useState(false);
  const [dataset, setDataset] = useState("totprec");
  const [activeMapSurfaceLayer, setActiveMapSurfaceLayer] = useState("incident_rate");
  const [lastHealthMapLayer, setLastHealthMapLayer] = useState("incident_rate");
  const [geoData, setGeoData] = useState(null);
  const [woredaPcodeCrosswalk, setWoredaPcodeCrosswalk] = useState(null);
  const [, setEnvData] = useState({});
  const [populationSurfacesByYear, setPopulationSurfacesByYear] = useState({});
  const [legacyPopulationSurface, setLegacyPopulationSurface] = useState({});
  const [syncedHoverDate, setSyncedHoverDate] = useState(null);
  const chartScopeKey = `${region}|${dataset}|${lastHealthMapLayer}|${disease}`;
  const chartDatesRef = useRef({ startDate, endDate });
  chartDatesRef.current = { startDate, endDate };
  const chartDateResetKeyRef = useRef("");
  const chartDateScopeKeyRef = useRef("");
  const chartDatesHydratedRef = useRef(false);

  const applyDefaultChartDateRange = useCallback(
    ({ force = false } = {}) => {
      // Wait until forecast bootstrap finished and forecast rows exist.
      if (!forecastBootstrapReady) return false;
      if (!epidemiaData?.forecasts?.length) return false;

      const range = resolveChartDateRange(epidemiaData);
      if (!range?.startDate || !range?.endDate) return false;

      const nextEnd = String(range.endDate).slice(0, 10);
      const currentEnd = String(chartDatesRef.current.endDate || "").slice(0, 10);

      // Never replace a newer window with an older/stale bootstrap span.
      if (!force && chartDatesHydratedRef.current && currentEnd && nextEnd < currentEnd) {
        return false;
      }

      setStartDate(range.startDate);
      setEndDate(range.endDate);
      lastAutoExtendedDistrictRef.current = null;
      chartDatesHydratedRef.current = true;
      return true;
    },
    [epidemiaData, forecastBootstrapReady]
  );

  const handleStartDateChange = useCallback((value) => {
    setStartDate(value);
  }, []);

  const handleEndDateChange = useCallback((value) => {
    setEndDate(value);
  }, []);

  const handleChartDateRelayout = useCallback((ev) => {
    const parsed = parseXAxisRangeFromRelayoutEvent(ev);
    if (parsed == null) return;
    // Chart redraws often emit autorange; keep the picker range instead of resetting.
    if (parsed === "autorange") return;

    const nextStart = normalizeChartAxisDate(parsed[0]);
    const nextEnd = normalizeChartAxisDate(parsed[1]);
    const { startDate: curStart, endDate: curEnd } = chartDatesRef.current;
    if (nextStart === curStart && nextEnd === curEnd) return;
    setStartDate(nextStart);
    setEndDate(nextEnd);
  }, []);

  const { makePlotReadyHandler, makePlotPurgeHandler, registerHighlightResolver } =
    useChartPlotRegistry(startDate, endDate, handleChartDateRelayout, syncedHoverDate);

  const [rightPanelView, setRightPanelView] = useState("charts");
  const [comparisonDistricts, setComparisonDistricts] = useState(["", "", ""]);
  const [comparisonHighlightMode, setComparisonHighlightMode] = useState("alert-priority");
  const isCompactLayout = useMediaQuery("(max-width: 1100px)");
  const [mobileMainView, setMobileMainView] = useState("summary");
  const [tourOpen, setTourOpen] = useState(false);
  const [tourNonce, setTourNonce] = useState(0);
  const { ediEnabled, studyLabel, isStudyArm, condition: studyCondition } =
    useStudyCondition();
  const tourSteps = useMemo(
    () => getDashboardTourSteps({ ediEnabled }),
    [ediEnabled]
  );

  useEffect(() => {
    if (!ediEnabled && rightPanelView === "decision") {
      setRightPanelView("charts");
    }
  }, [ediEnabled, rightPanelView]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (studyCondition) {
      document.documentElement.dataset.studyCondition = studyCondition;
    } else {
      delete document.documentElement.dataset.studyCondition;
    }
    return () => {
      delete document.documentElement.dataset.studyCondition;
    };
  }, [studyCondition]);

  const prepareTourStep = useCallback(
    (step) => {
      if (!step) return;
      if (step.panel) {
        setRightPanelView(step.panel);
      }
      if (isCompactLayout && step.mobileView) {
        setMobileMainView(step.mobileView);
      }
    },
    [isCompactLayout]
  );

  const startTour = useCallback(() => {
    if (isCompactLayout) {
      setMobileMainView("map");
    }
    setRightPanelView("charts");
    setTourNonce((n) => n + 1);
    setTourOpen(true);
  }, [isCompactLayout]);

  useEffect(() => {
    if (!shouldAutoStartDashboardTour()) return undefined;
    // Run once — do not re-fire when startTour identity changes (compact layout flips).
    const timer = window.setTimeout(() => {
      if (!shouldAutoStartDashboardTour()) return;
      markDashboardTourAutoStartAttempted();
      startTour();
    }, 1400);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot auto-start
  }, []);

  // Decision layers states
  const [showEarlyWarning, setShowEarlyWarning] = useState(true);
  const [showEarlyDetection, setShowEarlyDetection] = useState(true);

  // Alert history animation (last 4–8 weeks from observed case history)
  const [alertTimeMode, setAlertTimeMode] = useState("current"); // "current" | "animate"
  const [alertHistoryWeeks, setAlertHistoryWeeks] = useState(8);
  const [alertWeekIndex, setAlertWeekIndex] = useState(0);
  const [alertPlaying, setAlertPlaying] = useState(false);

  const handleMapSurfaceLayerChange = useCallback((layer) => {
    setActiveMapSurfaceLayer(layer);
    if (HEALTH_MAP_SURFACE_LAYERS.has(layer)) {
      setLastHealthMapLayer(layer);
    }
  }, []);

  const envMapLayerActive = isEnvMapSurfaceLayer(activeMapSurfaceLayer);
  const showRainfallLayer = activeMapSurfaceLayer === "rainfall";
  const showTemperatureLayer = activeMapSurfaceLayer === "temperature";
  const showNdviLayer = activeMapSurfaceLayer === "ndvi";

  // Raster time controls
  const [envTimeMode, setEnvTimeMode] = useState("average"); // "average" | "animate"
  const weekDates = useMemo(() => buildWeekDates(startDate, endDate), [startDate, endDate]);
  const availableEpiweeks = useMemo(
    () => collectEpidemiaDataDates(epidemiaData),
    [epidemiaData]
  );
  const [weekIndex, setWeekIndex] = useState(0);
  const [envPlaying, setEnvPlaying] = useState(false);
  const [envAverageStats, setEnvAverageStats] = useState(null);

  // Keep slider index valid when date range changes.
  useEffect(() => {
    setWeekIndex((idx) => {
      const max = Math.max(0, weekDates.length - 1);
      return Math.min(Math.max(0, idx), max);
    });
  }, [weekDates]);

  // Stop playback when leaving animate mode or no dates.
  useEffect(() => {
    if (envTimeMode !== "animate" || weekDates.length < 2) {
      setEnvPlaying(false);
    }
  }, [envTimeMode, weekDates]);

  useEffect(() => {
    if (!envPlaying) return undefined;
    if (envTimeMode !== "animate") return undefined;
    if (weekDates.length < 2) return undefined;

    const interval = window.setInterval(() => {
      setWeekIndex((idx) => (idx + 1) % weekDates.length);
    }, 800);

    return () => window.clearInterval(interval);
  }, [envPlaying, envTimeMode, weekDates]);

  const gibsPrefetchTime = useMemo(() => {
    if (envTimeMode !== "animate" || !envPlaying || weekDates.length < 2) {
      return null;
    }
    return weekDates[(weekIndex + 1) % weekDates.length];
  }, [envTimeMode, envPlaying, weekDates, weekIndex]);

  const averageSampleInfo = useMemo(() => {
    if (envTimeMode !== "average") return "";
    if (!envAverageStats) return `Sampling 7 dates across ${startDate} → ${endDate}.`;
    const attempted = envAverageStats?.attempted ?? 7;
    const loaded = envAverageStats?.loadedAverage ?? null;
    if (loaded == null) return `Sampling ${attempted} dates across ${startDate} → ${endDate}.`;
    return `Sampling ${attempted} dates across ${startDate} → ${endDate}. Loaded ~${loaded} / ${attempted} on average (visible tiles).`;
  }, [envTimeMode, envAverageStats, startDate, endDate]);

  const selectedSpecies = disease === "Plasmodium falciparum malaria" ? "pfm" : 
                          disease === "Plasmodium vivax malaria" ? "pv" : "pv";

  const chartDateResetKey = `${region}|${selectedSpecies}|${epidemiaData?.generated_at || ""}|${epidemiaData?.forecasts?.length || 0}|${forecastBootstrapReady ? 1 : 0}`;
  const chartDateScopeKey = `${region}|${selectedSpecies}`;

  useEffect(() => {
    if (!forecastBootstrapReady) return;
    if (chartDateResetKeyRef.current === chartDateResetKey) return;

    const scopeChanged = chartDateScopeKeyRef.current !== chartDateScopeKey;
    chartDateScopeKeyRef.current = chartDateScopeKey;

    // Region/species change always resets; data upgrades only move forward in time.
    const applied = applyDefaultChartDateRange({
      force: scopeChanged || !chartDatesHydratedRef.current,
    });
    if (applied || epidemiaData?.forecasts?.length) {
      chartDateResetKeyRef.current = chartDateResetKey;
    }
  }, [
    applyDefaultChartDateRange,
    chartDateResetKey,
    chartDateScopeKey,
    epidemiaData?.forecasts?.length,
    forecastBootstrapReady,
  ]);

  const adm3Lookup = useMemo(
    () => buildAdm3Lookup(geoData, woredaPcodeCrosswalk),
    [geoData, woredaPcodeCrosswalk]
  );

  const alertWeekDates = useMemo(
    () =>
      buildAlertWeekDates(
        epidemiaData?.forecasts,
        selectedSpecies,
        alertHistoryWeeks,
        startDate,
        endDate
      ),
    [epidemiaData?.forecasts, selectedSpecies, alertHistoryWeeks, startDate, endDate]
  );

  useEffect(() => {
    setAlertWeekIndex((idx) => {
      const max = Math.max(0, alertWeekDates.length - 1);
      return Math.min(Math.max(0, idx), max);
    });
  }, [alertWeekDates]);

  useEffect(() => {
    if (alertTimeMode !== "animate" || alertWeekDates.length < 2) {
      setAlertPlaying(false);
    }
  }, [alertTimeMode, alertWeekDates]);

  useEffect(() => {
    if (!alertPlaying) return undefined;
    if (alertTimeMode !== "animate") return undefined;
    if (alertWeekDates.length < 2) return undefined;

    const interval = window.setInterval(() => {
      setAlertWeekIndex((idx) => (idx + 1) % alertWeekDates.length);
    }, 900);

    return () => window.clearInterval(interval);
  }, [alertPlaying, alertTimeMode, alertWeekDates]);

  const alertAnimationWeek =
    alertTimeMode === "animate" && alertWeekDates.length
      ? alertWeekDates[alertWeekIndex]
      : null;

  const speciesAlerts = useMemo(
    () =>
      enrichAlertsWithForecastWarnings(
        epidemiaData?.alerts,
        epidemiaData?.forecasts,
        selectedSpecies
      ),
    [epidemiaData?.alerts, epidemiaData?.forecasts, selectedSpecies]
  );

  const mapAlerts = useMemo(() => {
    let alerts;
    if (alertTimeMode !== "animate" || !alertAnimationWeek) {
      alerts = speciesAlerts;
    } else {
      const latestWeek = alertWeekDates.length
        ? alertWeekDates[alertWeekDates.length - 1]
        : null;
      // Latest replay week uses pipeline flags (rolling 4-week ED/EW). Older weeks replay
      // only what exceeded threshold that week.
      if (latestWeek && alertAnimationWeek === latestWeek) {
        alerts = speciesAlerts;
      } else {
        alerts = buildAlertsForWeek(
          epidemiaData?.forecasts,
          epidemiaData?.alerts,
          selectedSpecies,
          alertAnimationWeek
        );
      }
    }

    return filterAlertsByAdminRegion(alerts, adm3Lookup, selectedAdminRegion, geoData);
  }, [
    alertTimeMode,
    alertAnimationWeek,
    alertWeekDates,
    epidemiaData?.forecasts,
    epidemiaData?.alerts,
    speciesAlerts,
    selectedSpecies,
    adm3Lookup,
    selectedAdminRegion,
    geoData,
  ]);

  const alertWeekCounts = useMemo(() => countAlertTypes(mapAlerts), [mapAlerts]);

  // Map clears to basemap-only ("No Selection") briefly, then shows the chosen view. Toolbar updates immediately.
  // "No Selection" alone applies immediately with no second step.
  const handleChangeAdminRegion = useCallback((value) => {
    if (mapRegionStepTimerRef.current) {
      clearTimeout(mapRegionStepTimerRef.current);
      mapRegionStepTimerRef.current = null;
    }
    setSelectedAdminRegion(value);
    if (value === "No Selection") {
      setMapFilterRegion("No Selection");
      return;
    }
    setMapFilterRegion("No Selection");
    mapRegionStepTimerRef.current = window.setTimeout(() => {
      mapRegionStepTimerRef.current = null;
      setMapFilterRegion(value);
    }, 150);
  }, []);

  useEffect(
    () => () => {
      if (mapRegionStepTimerRef.current) {
        clearTimeout(mapRegionStepTimerRef.current);
      }
    },
    []
  );

  const loadMapEpidemia = useCallback(async () => {
    const requestId = mapRequestIdRef.current + 1;
    mapRequestIdRef.current = requestId;

    const cached = await readBootstrapCache(bootstrapCacheKey("map", forecastWeeks));
    if (cached && mapRequestIdRef.current === requestId) {
      setEpidemiaData((current) => mergeForecastBootstrap(current, cached));
      setEpidemiaError("");
    }

    try {
      const data = await fetchMapEpidemiaReport({
        outputDir: projectOutputDir,
        horizonWeeks: forecastWeeks,
      });
      if (mapRequestIdRef.current === requestId) {
        setEpidemiaData((current) => mergeForecastBootstrap(current, data));
        setEpidemiaError("");
      }
    } catch (err) {
      console.error("Failed to load map EPIDEMIA report:", err);
      if (mapRequestIdRef.current === requestId && !cached) {
        setEpidemiaError(formatForecastApiError(err, "load map forecast report"));
      }
    }
  }, [projectOutputDir, forecastWeeks]);

  const loadCacheStatus = useCallback(async () => {
    try {
      const status = await fetchEpidemiaCacheStatus({
        dataDir: projectDataDir,
        outputDir: projectOutputDir,
        horizonWeeks: forecastWeeks,
      });
      if (status) {
        setCacheStatus(status);
      }
    } catch (err) {
      console.warn("Failed to load forecast cache status:", err);
    }
  }, [projectDataDir, projectOutputDir, forecastWeeks]);

  const loadForecastBootstrap = useCallback(async () => {
    const requestId = forecastRequestIdRef.current + 1;
    forecastRequestIdRef.current = requestId;

    const cached = await readBootstrapCache(bootstrapCacheKey("bootstrap", forecastWeeks));
    if (cached && forecastRequestIdRef.current === requestId) {
      setEpidemiaData((current) => ({
        ...(current || {}),
        ...cached,
        forecasts: cached.forecasts || [],
        alerts: cached.alerts || [],
      }));
      setForecastBootstrapReady(true);
    }

    setEpidemiaLoading(!cached);
    setEpidemiaError("");
    try {
      const data = await fetchLatestEpidemiaReport({
        outputDir: projectOutputDir,
        horizonWeeks: forecastWeeks,
      });
      if (forecastRequestIdRef.current === requestId) {
        setEpidemiaData((current) => ({
          ...(current || {}),
          ...data,
          forecasts: data.forecasts || [],
          alerts: data.alerts || [],
        }));
        setForecastBootstrapReady(true);
      }
    } catch (err) {
      console.error("Failed to load latest EPIDEMIA report:", err);
      if (forecastRequestIdRef.current === requestId) {
        if (!cached) {
          setEpidemiaError(formatForecastApiError(err, "load latest forecast report"));
        }
        setForecastBootstrapReady(true);
      }
    } finally {
      if (forecastRequestIdRef.current === requestId) {
        setEpidemiaLoading(false);
      }
    }
  }, [projectOutputDir, forecastWeeks]);

  const refreshEpidemia = useCallback(async () => {
    let regionFilter = null;
    if (
      selectedAdminRegion &&
      selectedAdminRegion !== "All Regions" &&
      selectedAdminRegion !== "No Selection"
    ) {
      regionFilter = selectedAdminRegion;
    } else if (region && region !== "All Regions") {
      regionFilter = resolveAdminRegionForDistrict(adm3Lookup, region, geoData);
      if (!regionFilter) {
        const feature =
          findDistrictFromLookup(adm3Lookup, region) ||
          geoData?.features?.find((f) => f?.properties?.adm3_name === region);
        const pcode = feature?.properties?.adm3_pcode;
        if (pcode) {
          regionFilter = resolveAdminRegionByPcode(geoData, pcode);
        }
      }
    }

    const requestId = forecastRequestIdRef.current + 1;
    forecastRequestIdRef.current = requestId;
    setEpidemiaRefreshing(true);
    setEpidemiaError("");
    try {
      await runEpidemiaPipeline({
        horizonWeeks: forecastWeeks,
        dataDir: projectDataDir,
        outputDir: projectOutputDir,
        createReport: false,
        forceRefresh: true,
        regionFilter,
        runInBackground: true,
      });
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "");
      if (!detail.includes("already running")) {
        throw err;
      }
    }

    try {
      await waitForPipelineIdle({
        dataDir: projectDataDir,
        outputDir: projectOutputDir,
        horizonWeeks: forecastWeeks,
        onProgress: (status) => {
          if (status) setCacheStatus(status);
        },
      });

      if (forecastRequestIdRef.current !== requestId) return;

      await loadMapEpidemia();
      await loadForecastBootstrap();
      await loadCacheStatus();
    } catch (err) {
      console.error("Failed to run EPIDEMIA pipeline:", err);
      if (forecastRequestIdRef.current === requestId) {
        const detail = err?.response?.data?.detail;
        setEpidemiaError(
          formatForecastApiError(err, "refresh forecast") +
            (detail ? ` ${detail}` : "")
        );
      }
    } finally {
      if (forecastRequestIdRef.current === requestId) {
        setEpidemiaRefreshing(false);
      }
      await loadCacheStatus();
    }
  }, [
    forecastWeeks,
    projectDataDir,
    projectOutputDir,
    selectedAdminRegion,
    region,
    adm3Lookup,
    geoData,
    loadCacheStatus,
    loadForecastBootstrap,
    loadMapEpidemia,
  ]);

  useEffect(() => {
    loadCacheStatus();
  }, [loadCacheStatus]);

  useEffect(() => {
    const pipelineState = cacheStatus?.pipeline?.status;
    const intervalMs =
      epidemiaRefreshing || pipelineState === "running" ? 5000 : 30000;
    const timer = window.setInterval(loadCacheStatus, intervalMs);
    return () => window.clearInterval(timer);
  }, [cacheStatus?.pipeline?.status, epidemiaRefreshing, loadCacheStatus]);

  useEffect(() => {
    if (!bootstrapEpidemiaData) return;
    setEpidemiaData(bootstrapEpidemiaData);
    setEpidemiaError("");
    setForecastBootstrapReady(true);
    skipInitialForecastLoadRef.current = true;
    onBootstrapConsumedRef.current?.();
  }, [bootstrapEpidemiaData]);

  useEffect(() => {
    if (skipInitialForecastLoadRef.current) {
      skipInitialForecastLoadRef.current = false;
      return undefined;
    }

    let cancelled = false;
    loadMapEpidemia().finally(() => {
      if (cancelled) return;
      return runAfterFirstPaint(() => {
        if (!cancelled) loadForecastBootstrap();
      });
    });

    return () => {
      cancelled = true;
    };
  }, [loadForecastBootstrap, loadMapEpidemia]);

  useEffect(() => {
    if (!forecastBootstrapReady) return undefined;

    let cancelled = false;
    const loadPopulationSurface = async () => {
      try {
        const byYear = await fetchJsonIfAvailable("/ethiopia_admin3_population_surface_by_year.json");
        if (isObjectRecord(byYear) && Object.values(byYear).some(isObjectRecord)) {
          if (!cancelled) {
            setPopulationSurfacesByYear(byYear);
            setLegacyPopulationSurface({});
          }
          return;
        }

        const surface = await fetchJsonIfAvailable("/ethiopia_admin3_population_surface.json");
        if (cancelled) return;
        setPopulationSurfacesByYear({});
        setLegacyPopulationSurface(isObjectRecord(surface) ? surface : {});
      } catch (err) {
        console.error("Failed to load population data", err);
        if (!cancelled) {
          setPopulationSurfacesByYear({});
          setLegacyPopulationSurface({});
        }
      }
    };

    loadPopulationSurface();

    return () => {
      cancelled = true;
    };
  }, [forecastBootstrapReady]);

  useEffect(() => {
    if (!forecastBootstrapReady) return undefined;

    let cancelled = false;
    const loadWoredaPcodeCrosswalk = async () => {
      try {
        const crosswalk = await fetchJsonIfAvailable("/ethiopia_woreda_pcode.json");
        if (!cancelled) {
          setWoredaPcodeCrosswalk(
            crosswalk?.byName && crosswalk?.byKey ? crosswalk : null
          );
        }
      } catch (err) {
        console.error("Failed to load woreda pcode crosswalk", err);
        if (!cancelled) setWoredaPcodeCrosswalk(null);
      }
    };

    loadWoredaPcodeCrosswalk();

    return () => {
      cancelled = true;
    };
  }, [forecastBootstrapReady]);

  // Extract unique regions from geoData when it loads
  React.useEffect(() => {
    if (geoData && geoData.features) {
      const regionSet = new Set(geoData.features.map((f) => f.properties.adm1_name).filter(Boolean));
      const regionList = Array.from(regionSet).sort();
      setRegions(["No Selection", "All Regions", ...regionList]);
    }
  }, [geoData]);

  // Basemap-only mode: clear overlays + district fills + selections.
  React.useEffect(() => {
    if (selectedAdminRegion !== "No Selection") return;
    // Basemap-only should *hide* layers, not reset user choices.
    // We still stop animation to avoid unnecessary tile churn.
    setEnvPlaying(false);
    setRegion("All Regions");
    setSelectedGeometry(null);
  }, [selectedAdminRegion]);

  // Build district dropdown list from selected region.
  React.useEffect(() => {
    if (!geoData?.features) {
      setDistricts(["All Regions"]);
      return;
    }

    const selectedRegionDistricts = geoData.features
      .filter((f) => {
        const adm1 = f?.properties?.adm1_name;
        return selectedAdminRegion === "All Regions" || adm1 === selectedAdminRegion;
      })
      .map((f) => f?.properties?.adm3_name)
      .filter(Boolean);

    const uniqueDistricts = ["All Regions", ...new Set(selectedRegionDistricts)].sort();
    setDistricts(uniqueDistricts);
  }, [geoData, selectedAdminRegion]);

  const updateRegion = useCallback((selectedRegion) => {
    userPrefersAllDistrictsRef.current = selectedRegion === "All Regions";
    setRegion(selectedRegion);
    if (selectedRegion === "All Regions") {
      setSelectedGeometry(null);
      return;
    }

    let feature = null;
    if (geoData) {
      feature =
        geoData.features.find((f) => f.properties.adm3_name === selectedRegion) ||
        findDistrictFromLookup(adm3Lookup, selectedRegion);
      if (feature) {
        setSelectedGeometry(feature.geometry.coordinates);
      }
    }

    const adm1 =
      feature?.properties?.adm1_name ||
      resolveAdminRegionForDistrict(adm3Lookup, selectedRegion, geoData);
    if (adm1 && adm1 !== selectedAdminRegion) {
      setSelectedAdminRegion(adm1);
      setMapFilterRegion(adm1);
    }
  }, [geoData, adm3Lookup, selectedAdminRegion]);

  const projectForecastDistricts = useMemo(() => {
    const names = (epidemiaData?.forecasts || [])
      .map((row) => row?.district)
      .filter(Boolean);
    return [...new Set(names)].sort();
  }, [epidemiaData]);

  // Single-district projects (e.g. Bichena/Enemay): select the woreda, not the admin region.
  useEffect(() => {
    if (projectForecastDistricts.length !== 1) return;
    if (userPrefersAllDistrictsRef.current) return;

    const onlyDistrict = projectForecastDistricts[0];
    if (region === onlyDistrict) return;

    const inDropdown = districts.includes(onlyDistrict);
    const inLookup = Boolean(findDistrictFromLookup(adm3Lookup, onlyDistrict));
    if (!inDropdown && !inLookup) return;

    const selectionHasForecast = projectForecastDistricts.includes(region);
    if (selectionHasForecast) return;

    updateRegion(onlyDistrict);
  }, [adm3Lookup, districts, projectForecastDistricts, region, updateRegion]);

  const selectedAlert = useMemo(() => {
    if (!speciesAlerts.length || region === "All Regions") return null;
    return (
      speciesAlerts.find(
        (a) =>
          a.district === region ||
          findDistrictFromLookup(adm3Lookup, a.district)?.properties?.adm3_name === region
      ) || null
    );
  }, [adm3Lookup, region, selectedSpecies, speciesAlerts]);

  const seasonalContext = useMemo(() => {
    if (!epidemiaData?.forecasts || region === "All Regions") return null;
    const districtFc = findDistrictForecastRow(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies
    );
    if (!districtFc?.observed_history?.length) return null;

    return buildSeasonalContext(
      districtFc.observed_history.map((point) => ({
        date: point.week_start,
        observed: point.observed,
        detection_threshold: point.detection_threshold ?? null,
      }))
    );
  }, [adm3Lookup, epidemiaData, region, selectedSpecies]);

  const populationSurfaceYears = useMemo(
    () =>
      Object.keys(populationSurfacesByYear)
        .map(Number)
        .filter(Number.isFinite)
        .sort((a, b) => a - b),
    [populationSurfacesByYear]
  );

  const populationYear = useMemo(
    () => nearestAvailableYear(yearFromDate(endDate), populationSurfaceYears),
    [endDate, populationSurfaceYears]
  );

  const populationSurface = useMemo(() => {
    if (populationYear != null) {
      return populationSurfacesByYear[String(populationYear)] || {};
    }
    return legacyPopulationSurface;
  }, [legacyPopulationSurface, populationSurfacesByYear, populationYear]);

  const populationData = useMemo(() => {
    const out = {};

    const assign = (name, population) => {
      if (!name || !Number.isFinite(population)) return;
      const variants = [name, ...getDistrictNameVariants(name)];
      variants.forEach((variant) => {
        const normalized = normalizeDistrictKey(variant);
        out[variant] = population;
        out[normalized] = population;
      });
    };

    const assignIfMissing = (name, population) => {
      if (!name || !Number.isFinite(population)) return;
      const variants = [name, ...getDistrictNameVariants(name)];
      variants.forEach((variant) => {
        const normalized = normalizeDistrictKey(variant);
        if (out[variant] == null && out[normalized] == null) {
          out[variant] = population;
          out[normalized] = population;
        }
      });
    };

    // Match forecast table and models: surveillance population_at_risk from alerts.
    (epidemiaData?.alerts || []).forEach((alert) => {
      const population = Number(alert.population_at_risk);
      if (!Number.isFinite(population)) return;

      const district = findDistrictFromLookup(adm3Lookup, alert.district);
      const mapName = district?.properties?.adm3_name || alert.district;
      assign(mapName, population);
      assign(alert.district, population);
    });

    // WorldPop only fills districts missing from the forecast report.
    Object.entries(populationSurface).forEach(([name, population]) => {
      assignIfMissing(name, Number(population));
    });

    return out;
  }, [adm3Lookup, epidemiaData, populationSurface]);

  const incidentRateData = useMemo(
    () =>
      buildIncidentRateData({
        epidemiaData,
        adm3Lookup,
        populationData,
        selectedSpecies,
        startDate,
        endDate,
        surfaceValueForDistrict,
      }),
    [adm3Lookup, epidemiaData, populationData, selectedSpecies, startDate, endDate]
  );

  const forecastSeriesOptions = useMemo(
    () => ({
      valueMode: forecastValueMode,
      populationData,
      surfaceValueForDistrict,
    }),
    [forecastValueMode, populationData]
  );

  const selectedForecast = useMemo(() => {
    if (!epidemiaData?.forecasts || region === "All Regions") return null;
    const series = buildDistrictForecastSeries(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies,
      startDate,
      endDate,
      forecastSeriesOptions
    );
    return series?.rows?.length ? series.rows : null;
  }, [
    adm3Lookup,
    endDate,
    epidemiaData,
    forecastSeriesOptions,
    region,
    selectedSpecies,
    startDate,
  ]);

  // If the picker range excludes all points, snap to the report's data span.
  useEffect(() => {
    if (region === "All Regions" || !epidemiaData?.forecasts?.length) return;

    const reportDistrict = resolveReportDistrictForSelection(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies
    );
    if (!reportDistrict) return;

    if (selectedForecast?.length) return;

    const fullSeries = buildDistrictForecastSeries(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies,
      null,
      null,
      forecastSeriesOptions
    );
    if (!fullSeries?.rows?.length) return;

    applyDefaultChartDateRange();
  }, [
    adm3Lookup,
    applyDefaultChartDateRange,
    epidemiaData,
    forecastSeriesOptions,
    region,
    selectedForecast,
    selectedSpecies,
  ]);

  // If a district forecast extends past the current end date, extend the picker so the
  // horizon is visible. Never shrink the default one-year window (e.g. 2025-10-19 → 2026-10-19).
  useEffect(() => {
    if (region === "All Regions") {
      lastAutoExtendedDistrictRef.current = null;
      return;
    }
    if (!selectedForecast?.length) return;

    const districtKey = `${region}|${selectedSpecies}|${forecastWeeks}`;
    if (lastAutoExtendedDistrictRef.current === districtKey) return;

    const maxForecastDate = selectedForecast
      .filter((row) => row?.median !== null && row?.median !== undefined && row?.date)
      .map((row) => String(row.date).slice(0, 10))
      .sort()
      .slice(-1)[0];
    if (!maxForecastDate) return;

    lastAutoExtendedDistrictRef.current = districtKey;
    setEndDate((currentEnd) => {
      const current = String(currentEnd || "").slice(0, 10);
      if (current && maxForecastDate <= current) return currentEnd;
      return maxForecastDate;
    });
  }, [forecastWeeks, region, selectedSpecies, selectedForecast]);

  const mapHealthLayer = HEALTH_MAP_SURFACE_LAYERS.has(activeMapSurfaceLayer)
    ? activeMapSurfaceLayer
    : lastHealthMapLayer;
  const healthLayerData =
    mapHealthLayer === "incident_rate" ? incidentRateData : populationData;
  const reportExportActive = Boolean(reportExportConfig);
  const healthChoroplethEnabled = !envMapLayerActive;
  const mapDataset = reportExportConfig?.dataset ?? mapHealthLayer;
  const mapEnvData = reportExportConfig?.envData ?? healthLayerData;
  const mapSpecies = reportExportConfig?.species ?? selectedSpecies;
  const mapFilterForView = reportExportActive
    ? reportExportConfig?.mapFilter ?? "All Regions"
    : mapFilterRegion;
  const mapSelectedDistrict = reportExportActive
    ? reportExportConfig?.selectedDistrict ?? null
    : region !== "All Regions"
      ? region
      : null;

  const reportScopeAvailability = useMemo(
    () => ({
      canRegion: canUseReportScope(REPORT_SCOPE_REGION, {
        region,
        selectedAdminRegion,
      }),
      canDistrict: canUseReportScope(REPORT_SCOPE_DISTRICT, {
        region,
        selectedAdminRegion,
      }),
    }),
    [region, selectedAdminRegion]
  );

  useEffect(() => {
    if (reportScope === REPORT_SCOPE_DISTRICT && !reportScopeAvailability.canDistrict) {
      setReportScope(REPORT_SCOPE_COUNTRY);
    } else if (reportScope === REPORT_SCOPE_REGION && !reportScopeAvailability.canRegion) {
      setReportScope(REPORT_SCOPE_COUNTRY);
    }
  }, [reportScope, reportScopeAvailability]);

  useEffect(() => {
    setWoredaPageMode(defaultWoredaPageMode(reportScope));
  }, [reportScope]);

  const forecastTableRows = useMemo(() => {
    const alerts = speciesAlerts;
    const forecasts = (epidemiaData?.forecasts || []).filter((f) => f.species === selectedSpecies);
    const forecastByDistrict = new Map(forecasts.map((f) => [f.district, f]));

    return alerts.map((alert) => {
      const forecast = forecastByDistrict.get(alert.district);
      const feature = findDistrictFromLookup(adm3Lookup, alert.district);
      const mapDistrict = feature?.properties?.adm3_name || alert.district;
      const status = alertStatus(alert);
      const statusRank = status === "Early Warning" ? 3 : status === "Early Detection" ? 2 : 1;
      const latestForecast = finiteNumber(alert.latest_forecast);
      const detectionThreshold = finiteNumber(alert.detection_threshold);
      const warningThreshold = finiteNumber(alert.warning_threshold);
      const activeThreshold = warningThreshold;
      const magnitude =
        latestForecast != null && activeThreshold != null ? latestForecast - activeThreshold : null;
      const magnitudePercent =
        magnitude != null && activeThreshold > 0 ? (magnitude / activeThreshold) * 100 : null;
      const persistenceWeeks = (forecast?.forecast || []).filter((point) => {
        const median = finiteNumber(point.median);
        const warningThreshold = finiteNumber(point.warning_threshold);
        const detectionThreshold = finiteNumber(point.detection_threshold);
        if (median == null) return false;
        if (warningThreshold != null && median > warningThreshold) return true;
        if (detectionThreshold != null && median > detectionThreshold) return true;
        return false;
      }).length;
      const populationAtRisk = finiteNumber(alert.population_at_risk);
      const positiveMagnitudePercent = Math.max(0, magnitudePercent || 0);
      const priority =
        (status === "Early Warning" ? 1000 : status === "Early Detection" ? 500 : 0) +
        positiveMagnitudePercent +
        persistenceWeeks * 10 +
        Math.log10(Math.max(populationAtRisk || 1, 1));
      const { values: caseSparkline, weeks: caseSparklineWeeks } = buildDistrictCaseSparkline(
        forecast?.observed_history
      );

      return {
        rawDistrict: alert.district,
        mapDistrict,
        region: feature?.properties?.adm1_name || "",
        species: alert.species,
        status,
        statusRank,
        earlyWarning: Boolean(alert.early_warning),
        earlyDetection: Boolean(alert.early_detection),
        edLevel: alert.ed_level || "Low",
        ewLevel: alert.ew_level || "Low",
        edAlertCount: alert.ed_alert_count ?? 0,
        ewAlertCount: alert.ew_alert_count ?? 0,
        latestObserved: finiteNumber(alert.latest_observed),
        latestForecast,
        detectionThreshold,
        warningThreshold,
        activeThreshold,
        magnitude,
        magnitudePercent,
        persistenceWeeks,
        populationAtRisk,
        priority,
        caseSparkline,
        caseSparklineWeeks,
      };
    })
      .map((row) =>
        transformForecastTableRow(row, forecastValueMode, row.populationAtRisk)
      );
  }, [adm3Lookup, epidemiaData, forecastValueMode, selectedSpecies, speciesAlerts]);

  const selectedDecisionInsight = useMemo(() => {
    if (region === "All Regions") return null;
    return (
      forecastTableRows.find(
        (row) =>
          row.mapDistrict === region ||
          row.rawDistrict === region ||
          findDistrictFromLookup(adm3Lookup, row.rawDistrict)?.properties?.adm3_name === region
      ) || null
    );
  }, [adm3Lookup, forecastTableRows, region]);

  const selectedDecisionNeighborSummary = useMemo(() => {
    if (region === "All Regions") return null;
    const regionName =
      selectedDecisionInsight?.region ||
      resolveAdminRegionForDistrict(adm3Lookup, region) ||
      (selectedAdminRegion !== "All Regions" && selectedAdminRegion !== "No Selection"
        ? selectedAdminRegion
        : null);
    return buildNeighborSummaryFromTableRows({
      rows: forecastTableRows,
      regionName,
      districtName: region,
    });
  }, [
    adm3Lookup,
    forecastTableRows,
    region,
    selectedAdminRegion,
    selectedDecisionInsight,
  ]);

  const selectedDecisionForecastPoints = useMemo(() => {
    if (!selectedForecast?.length) return [];
    return selectedForecast.filter(
      (point) => point?.median != null && Number.isFinite(Number(point.median))
    );
  }, [selectedForecast]);

  const selectedDecisionObservedHistory = useMemo(() => {
    if (!epidemiaData?.forecasts || region === "All Regions") return [];
    const districtFc = findDistrictForecastRow(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies
    );
    return districtFc?.observed_history || [];
  }, [adm3Lookup, epidemiaData, region, selectedSpecies]);

  const isHistoricalAlertWeek =
    alertTimeMode === "animate" && Boolean(alertAnimationWeek);

  const situationAlert = useMemo(() => {
    if (!isHistoricalAlertWeek || region === "All Regions") return selectedAlert;
    return (
      mapAlerts.find(
        (alert) =>
          alert.district === region ||
          findDistrictFromLookup(adm3Lookup, alert.district)?.properties?.adm3_name ===
            region
      ) || null
    );
  }, [adm3Lookup, isHistoricalAlertWeek, mapAlerts, region, selectedAlert]);

  const situationWeekObservedPoint = useMemo(() => {
    if (!isHistoricalAlertWeek || !alertAnimationWeek) return null;
    return (
      selectedDecisionObservedHistory.find(
        (point) => point?.week_start === alertAnimationWeek
      ) || null
    );
  }, [alertAnimationWeek, isHistoricalAlertWeek, selectedDecisionObservedHistory]);

  const situationForecastPoints = useMemo(() => {
    if (region !== "All Regions") {
      return selectedDecisionForecastPoints;
    }

    let scopedRows = [...forecastTableRows];
    if (
      selectedAdminRegion &&
      selectedAdminRegion !== "All Regions" &&
      selectedAdminRegion !== "No Selection"
    ) {
      scopedRows = scopedRows.filter((row) => row.region === selectedAdminRegion);
    }
    scopedRows.sort((a, b) => b.priority - a.priority);

    const topRow =
      scopedRows.find(
        (row) => row.status === "Early Warning" || row.status === "Early Detection"
      ) || scopedRows[0];
    if (!topRow) return [];

    const districtFc = findDistrictForecastRow(
      epidemiaData,
      adm3Lookup,
      topRow.mapDistrict || topRow.rawDistrict,
      selectedSpecies
    );
    return (districtFc?.forecast || []).filter(
      (point) => point?.median != null && Number.isFinite(Number(point.median))
    );
  }, [
    adm3Lookup,
    epidemiaData,
    forecastTableRows,
    region,
    selectedAdminRegion,
    selectedDecisionForecastPoints,
    selectedSpecies,
  ]);

  const situationObservedHistory = useMemo(() => {
    if (region !== "All Regions") {
      return selectedDecisionObservedHistory;
    }

    let scopedRows = [...forecastTableRows];
    if (
      selectedAdminRegion &&
      selectedAdminRegion !== "All Regions" &&
      selectedAdminRegion !== "No Selection"
    ) {
      scopedRows = scopedRows.filter((row) => row.region === selectedAdminRegion);
    }
    scopedRows.sort((a, b) => b.priority - a.priority);

    const topRow =
      scopedRows.find(
        (row) => row.status === "Early Warning" || row.status === "Early Detection"
      ) || scopedRows[0];
    if (!topRow) return [];

    const districtFc = findDistrictForecastRow(
      epidemiaData,
      adm3Lookup,
      topRow.mapDistrict || topRow.rawDistrict,
      selectedSpecies
    );
    return districtFc?.observed_history || [];
  }, [
    adm3Lookup,
    epidemiaData,
    forecastTableRows,
    region,
    selectedAdminRegion,
    selectedDecisionObservedHistory,
    selectedSpecies,
  ]);

  const topPriorityDistrict = useMemo(() => {
    const options = buildComparisonDistrictOptions(forecastTableRows, selectedAdminRegion);
    return options[0]?.value || null;
  }, [forecastTableRows, selectedAdminRegion]);

  // If the current district name is missing from the dropdown, recover or reset.
  React.useEffect(() => {
    if (districts.includes(region)) return;

    if (
      projectForecastDistricts.length === 1 &&
      projectForecastDistricts[0] === region
    ) {
      return;
    }

    userPrefersAllDistrictsRef.current = false;
    if (topPriorityDistrict && districts.includes(topPriorityDistrict)) {
      updateRegion(topPriorityDistrict);
      return;
    }

    setRegion("All Regions");
    setSelectedGeometry(null);
    // Stop auto-select from fighting this fallback when the priority district
    // is not present in the dropdown (name mismatch before geo loads, etc.).
    userPrefersAllDistrictsRef.current = true;
  }, [districts, projectForecastDistricts, region, topPriorityDistrict, updateRegion]);

  const defaultComparisonDistricts = useMemo(
    () =>
      buildRegionalComparisonDistricts(
        forecastTableRows,
        selectedAdminRegion,
        comparisonHighlightMode,
        {
          epidemiaData,
          adm3Lookup,
          selectedSpecies,
          startDate,
          endDate,
          count: 3,
          ...forecastSeriesOptions,
        }
      ),
    [
      adm3Lookup,
      comparisonHighlightMode,
      endDate,
      epidemiaData,
      forecastSeriesOptions,
      forecastTableRows,
      selectedAdminRegion,
      selectedSpecies,
      startDate,
    ]
  );

  const comparisonHighlightModeOptions = useMemo(
    () => buildComparisonHighlightModes(forecastValueMode),
    [forecastValueMode]
  );

  const comparisonDefaultsKey = useMemo(
    () =>
      [
        comparisonHighlightMode,
        selectedAdminRegion,
        selectedSpecies,
        startDate,
        endDate,
        chartScopeKey,
        forecastValueMode,
        epidemiaData?.generated_at || "",
      ].join("|"),
    [
      chartScopeKey,
      comparisonHighlightMode,
      endDate,
      epidemiaData?.generated_at,
      forecastValueMode,
      selectedAdminRegion,
      selectedSpecies,
      startDate,
    ]
  );

  React.useEffect(() => {
    setComparisonDistricts(defaultComparisonDistricts);
    // Reset only when highlight scope changes, not when district detail merges into epidemiaData.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by comparisonDefaultsKey
  }, [comparisonDefaultsKey]);

  const comparisonBackgroundDistricts = useMemo(() => {
    const selected = new Set(comparisonDistricts.filter(Boolean));
    const hasRegionalFilter =
      selectedAdminRegion &&
      selectedAdminRegion !== "All Regions" &&
      selectedAdminRegion !== "No Selection";

    const options = buildComparisonDistrictOptions(forecastTableRows, selectedAdminRegion);
    const candidates = (hasRegionalFilter ? options : options.slice(0, 10))
      .map((option) => option.value)
      .filter(Boolean);

    return candidates.filter((districtName) => !selected.has(districtName));
  }, [comparisonDistricts, forecastTableRows, selectedAdminRegion]);

  const districtsNeedingDetail = useMemo(() => {
    const names = new Set();

    const addIfNeeded = (selectedDistrict) => {
      const reportDistrict = resolveReportDistrictForSelection(
        epidemiaData,
        adm3Lookup,
        selectedDistrict,
        selectedSpecies
      );
      if (!reportDistrict) return;

      const row = findDistrictForecastRow(
        epidemiaData,
        adm3Lookup,
        selectedDistrict,
        selectedSpecies
      );
      if (!row) return;
      if (forecastHistoryCoversRange(row, startDate, endDate)) return;

      names.add(reportDistrict);
    };

    if (region && region !== "All Regions") {
      addIfNeeded(region);
    }
    comparisonDistricts.filter(Boolean).forEach(addIfNeeded);
    comparisonBackgroundDistricts.forEach(addIfNeeded);
    return [...names];
  }, [
    adm3Lookup,
    comparisonBackgroundDistricts,
    comparisonDistricts,
    endDate,
    epidemiaData,
    region,
    selectedSpecies,
    startDate,
  ]);

  const selectedDistrictNeedsDetail = useMemo(() => {
    if (region === "All Regions") return false;
    const reportDistrict = resolveReportDistrictForSelection(
      epidemiaData,
      adm3Lookup,
      region,
      selectedSpecies
    );
    return Boolean(reportDistrict && districtsNeedingDetail.includes(reportDistrict));
  }, [
    adm3Lookup,
    districtsNeedingDetail,
    epidemiaData,
    region,
    selectedSpecies,
  ]);

  const districtsNeedingDetailKey = districtsNeedingDetail.join("|");
  const epidemiaDataReady = Boolean(epidemiaData?.forecasts?.length);

  useEffect(() => {
    if (districtsNeedingDetail.length === 0) {
      setDistrictDetailLoading(false);
      return undefined;
    }
    if (!epidemiaDataReady) {
      return undefined;
    }

    const requestId = districtDetailRequestRef.current + 1;
    districtDetailRequestRef.current = requestId;
    setDistrictDetailLoading(true);

    Promise.all(
      districtsNeedingDetail.map((district) =>
        fetchDistrictForecastDetail({
          outputDir: projectOutputDir,
          district,
          species: selectedSpecies,
          startDate,
          endDate,
          horizonWeeks: forecastWeeks,
        }).catch((err) => {
          console.warn(`Failed to load forecast detail for ${district}:`, err);
          return null;
        })
      )
    )
      .then((details) => {
        if (districtDetailRequestRef.current !== requestId) return;
        setEpidemiaData((current) => {
          if (!current) return current;
          return details.reduce(
            (report, detail) => (detail ? mergeDistrictForecast(report, detail) : report),
            current
          );
        });
      })
      .finally(() => {
        if (districtDetailRequestRef.current === requestId) {
          setDistrictDetailLoading(false);
        }
      });

    return undefined;
  }, [
    districtsNeedingDetailKey,
    endDate,
    epidemiaDataReady,
    forecastWeeks,
    projectOutputDir,
    selectedSpecies,
    startDate,
  ]);

  const comparisonSeries = useMemo(() => {
    const uniqueDistricts = [...new Set(comparisonDistricts.filter(Boolean))].slice(0, 3);
    return uniqueDistricts
      .map((districtName) =>
        buildDistrictForecastSeries(
          epidemiaData,
          adm3Lookup,
          districtName,
          selectedSpecies,
          startDate,
          endDate,
          forecastSeriesOptions
        )
      )
      .filter(Boolean);
  }, [
    adm3Lookup,
    comparisonDistricts,
    epidemiaData,
    forecastSeriesOptions,
    selectedSpecies,
    startDate,
    endDate,
  ]);

  const comparisonBackgroundSeries = useMemo(() => {
    return comparisonBackgroundDistricts
      .map((districtName) =>
        buildDistrictForecastSeries(
          epidemiaData,
          adm3Lookup,
          districtName,
          selectedSpecies,
          startDate,
          endDate,
          forecastSeriesOptions
        )
      )
      .filter(Boolean);
  }, [
    adm3Lookup,
    comparisonBackgroundDistricts,
    epidemiaData,
    forecastSeriesOptions,
    selectedSpecies,
    startDate,
    endDate,
  ]);

  const toggleComparisonDistrict = useCallback(
    (districtName) => {
      setComparisonDistricts((current) => {
        const isSelected = current.includes(districtName);
        let next;

        if (isSelected) {
          const remaining = current.filter((district) => district !== districtName);
          next = [remaining[0] || "", remaining[1] || "", remaining[2] || ""];
          if (region === districtName) {
            const fallback = next.find(Boolean);
            updateRegion(fallback || topPriorityDistrict || "All Regions");
          }
        } else {
          const active = current.filter(Boolean);
          if (active.length < 3) {
            next = [...current];
            const slot = next.findIndex((district) => !district);
            next[slot] = districtName;
          } else {
            next = [current[0], current[1], districtName];
          }
          updateRegion(districtName);
        }

        return next;
      });
    },
    [region, topPriorityDistrict, updateRegion]
  );

  const handleSummaryDistrictSelect = useCallback(
    (districtName) => {
      if (!districtName) return;
      updateRegion(districtName);
      setMobileMainView("details");
      setRightPanelView("charts");
    },
    [updateRegion]
  );

  const handleMapDistrictSelect = useCallback(
    (districtName) => {
      updateRegion(districtName);
      if (isCompactLayout) {
        setMobileMainView("details");
        setRightPanelView("charts");
      }
    },
    [isCompactLayout, updateRegion]
  );

  const pipelineStatus = useMemo(
    () =>
      buildPipelineStatus({
        epidemiaLoading,
        epidemiaRefreshing,
        epidemiaError,
        generatedAt: epidemiaData?.generated_at,
        cacheStatus,
        forecastWeeks,
      }),
    [
      epidemiaLoading,
      epidemiaRefreshing,
      epidemiaError,
      epidemiaData?.generated_at,
      cacheStatus,
      forecastWeeks,
    ]
  );

  const speciesLabel = selectedSpecies === "pv" ? "P. vivax" : "P. falciparum";

  const comparisonPriorityCandidates = useMemo(
    () =>
      ediEnabled
        ? buildComparisonPriorityCandidates({
            comparisonDistricts,
            forecastTableRows,
            epidemiaData,
            adm3Lookup,
            selectedSpecies,
            speciesLabel,
          })
        : [],
    [
      adm3Lookup,
      comparisonDistricts,
      ediEnabled,
      epidemiaData,
      forecastTableRows,
      selectedSpecies,
      speciesLabel,
    ]
  );

  const currentAlertTooltipByDistrict = useMemo(
    () =>
      buildAlertTooltipLookup({
        forecastTableRows,
        alerts: speciesAlerts,
        selectedSpecies,
        speciesLabel,
        populationData,
        populationYear,
        surfaceValueForDistrict,
        epidemiaData,
        adm3Lookup,
        startDate,
        endDate,
      }),
    [
      forecastTableRows,
      speciesAlerts,
      selectedSpecies,
      speciesLabel,
      populationData,
      populationYear,
      epidemiaData,
      adm3Lookup,
      startDate,
      endDate,
    ]
  );

  const alertTooltipByDistrict = useMemo(() => {
    if (alertTimeMode !== "animate" || !alertAnimationWeek) {
      return currentAlertTooltipByDistrict;
    }
    return buildAnimatedAlertTooltipLookup(
      mapAlerts,
      speciesLabel,
      alertAnimationWeek,
      adm3Lookup
    );
  }, [
    alertTimeMode,
    alertAnimationWeek,
    currentAlertTooltipByDistrict,
    mapAlerts,
    speciesLabel,
    adm3Lookup,
  ]);

  const districtTooltipByDistrict = useMemo(
    () =>
      buildDistrictTooltipLookup({
        forecastTableRows,
        populationData,
        populationYear,
        surfaceValueForDistrict,
        epidemiaData,
        adm3Lookup,
        speciesAlerts,
        selectedSpecies,
        startDate,
        endDate,
      }),
    [
      forecastTableRows,
      populationData,
      populationYear,
      epidemiaData,
      adm3Lookup,
      speciesAlerts,
      selectedSpecies,
      startDate,
      endDate,
    ]
  );

  const mapTooltipContext = useMemo(
    () => ({
      forecastTableRows,
      speciesAlerts,
      epidemiaData,
      adm3Lookup,
      selectedSpecies: mapSpecies,
      populationData,
      populationYear,
      surfaceValueForDistrict,
      startDate,
      endDate,
    }),
    [
      forecastTableRows,
      speciesAlerts,
      epidemiaData,
      adm3Lookup,
      mapSpecies,
      populationData,
      populationYear,
      startDate,
      endDate,
    ]
  );


  // R-style EPIDEMIA report export (epidemia_report_demo.Rnw parity)
  const handleExportPDF = async () => {
    if (!epidemiaData?.alerts?.length || !adm3Lookup?.size) {
      window.alert("Forecast data is not loaded yet.");
      return;
    }

    setExporting(true);
    setExportProgressMessage("Preparing report…");

    const reportModel = buildEpidemiaReportModel({
      epidemiaData,
      adm3Lookup,
      horizonWeeks: forecastWeeks,
      country,
      scope: reportScope,
      selectedDistrict: region,
      selectedRegion: selectedAdminRegion,
      forecastValueMode,
    });

    const scopedAlerts = filterAlertsByScope(
      epidemiaData.alerts,
      adm3Lookup,
      reportModel.scopeContext
    );

    if (reportModel.districtRows.length === 0) {
      window.alert("No districts match the selected report scope.");
      setExporting(false);
      setExportProgressMessage("");
      return;
    }

    const woredaDistrictRows = filterWoredaDistrictRows(
      reportModel.districtRows,
      scopedAlerts,
      adm3Lookup,
      woredaPageMode,
      findDistrictFromLookup
    );

    reportModel.woredaPageMode = woredaPageMode;
    reportModel.woredaDistrictRows = woredaDistrictRows;
    reportModel.woredaDistrictCount = woredaDistrictRows.length;

    const mapCaptures = {};
    const captureModes = [
      { key: "pfm-ed", species: "pfm", dataset: "ed_alert_level", levelField: "ed_level" },
      { key: "pfm-ew", species: "pfm", dataset: "ew_alert_level", levelField: "ew_level" },
      { key: "pv-ed", species: "pv", dataset: "ed_alert_level", levelField: "ed_level" },
      { key: "pv-ew", species: "pv", dataset: "ew_alert_level", levelField: "ew_level" },
      { key: "pfm-incidence", species: "pfm", dataset: "incident_rate", incidence: true },
      { key: "pv-incidence", species: "pv", dataset: "incident_rate", incidence: true },
    ];

    try {
      const { captureReportMap } = await import("../../utils/exportEpidemiaReport");

      for (const mode of captureModes) {
        setExportProgressMessage(`Capturing map (${mode.key})…`);
        const envData = mode.incidence
          ? buildIncidentRateData({
              epidemiaData,
              adm3Lookup,
              populationData,
              selectedSpecies: mode.species,
              startDate,
              endDate,
              surfaceValueForDistrict,
            })
          : buildAlertLevelSurface(
              scopedAlerts,
              adm3Lookup,
              mode.species,
              mode.levelField
            );

        setReportExportConfig({
          dataset: mode.dataset,
          envData,
          species: mode.species,
          mapFilter: reportModel.scopeContext.mapFilter,
          selectedDistrict: reportModel.scopeContext.selectedDistrict,
        });

        const canvas = await captureReportMap(document.getElementById("epidemia-report-map"));
        if (canvas) {
          mapCaptures[mode.key] = canvas;
        }
      }

      setReportExportConfig(null);

      const districtChartImages =
        woredaPageMode === WOREDA_PAGE_MODES.none.value
          ? new Map()
          : await (
              await import("../../utils/buildReportControlChartFigure")
            ).renderAllDistrictControlChartImages({
              epidemiaData,
              adm3Lookup,
              districtRows: woredaDistrictRows,
              startDate,
              endDate,
              ...forecastSeriesOptions,
              onProgress: ({ current, total, district }) => {
                setExportProgressMessage(
                  `Rendering control charts (${current}/${total}): ${district}`
                );
              },
            });

      setExportProgressMessage("Writing PDF…");
      const { exportEpidemiaReport } = await import("../../utils/exportEpidemiaReport");
      await exportEpidemiaReport({
        reportModel,
        mapCaptures,
        districtChartImages,
        generatedAt: epidemiaData.generated_at,
        country,
      });
    } catch (err) {
      console.error("Failed to export EPIDEMIA report:", err);
      window.alert(err?.message || "Failed to export EPIDEMIA report.");
    } finally {
      setReportExportConfig(null);
      setExportProgressMessage("");
      setExporting(false);
    }
  };

  const showDataLoadingBanner = !forecastBootstrapReady;

  return (
    <div
      id="dashboard"
      className={`dashboard-shell${isCompactLayout ? " dashboard-shell--compact" : ""}`}
    >
      <TopToolbar
        disease={disease}
        onChangeDisease={setDisease}
        country={country}
        onChangeCountry={setCountry}
        selectedAdminRegion={selectedAdminRegion}
        onChangeAdminRegion={handleChangeAdminRegion}
        availableRegions={regions}
        selectedDistrict={region}
        onChangeDistrict={updateRegion}
        availableDistricts={districts}
        onExportPDF={handleExportPDF}
        exporting={exporting}
        exportLabel={exportProgressMessage || (exporting ? "Exporting…" : "Export EPIDEMIA Report")}
        reportScope={reportScope}
        onChangeReportScope={setReportScope}
        reportScopeAvailability={reportScopeAvailability}
        woredaPageMode={woredaPageMode}
        onChangeWoredaPageMode={setWoredaPageMode}
        projectName={projectConfig?.projectName}
        onNewProject={onOpenProjectWizard}
        onUseDefaultDataset={onUseDefaultDataset}
        usingCustomProject={usingCustomProject}
        showDefaultDatasetButton={showDefaultDatasetButton}
        onStartTour={startTour}
      />

      {isStudyArm ? (
        <div
          className={`study-condition-banner study-condition-banner--${studyCondition}`}
          role="status"
          data-study-condition={studyCondition}
        >
          Study condition: <strong>{studyLabel}</strong>
          {ediEnabled
            ? " — explainable alerts, uncertainty cues, and Decision panel available"
            : " — forecasts and alerts only (Decision / EDI surfaces hidden)"}
        </div>
      ) : null}

      {showDataLoadingBanner ? (
        <div className="dashboard-load-banner" role="status" aria-live="polite">
          Loading forecast data…
        </div>
      ) : null}

      {epidemiaError ? (
        <div className="dashboard-load-banner dashboard-load-banner--error" role="alert">
          {epidemiaError}
        </div>
      ) : null}

      <div className="dashboard-layout">
        <main className="main-content">
        {isCompactLayout ? (
          <nav className="mobile-view-switcher fade-in-up delay-1" role="tablist" aria-label="Dashboard views">
            <button
              type="button"
              role="tab"
              aria-selected={mobileMainView === "summary"}
              className={mobileMainView === "summary" ? "mobile-view-tab active" : "mobile-view-tab"}
              onClick={() => setMobileMainView("summary")}
            >
              Summary
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileMainView === "map"}
              className={mobileMainView === "map" ? "mobile-view-tab active" : "mobile-view-tab"}
              onClick={() => setMobileMainView("map")}
            >
              Map
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileMainView === "details" && rightPanelView !== "about"}
              className={
                mobileMainView === "details" && rightPanelView !== "about"
                  ? "mobile-view-tab active"
                  : "mobile-view-tab"
              }
              onClick={() => {
                setMobileMainView("details");
                setRightPanelView("charts");
              }}
            >
              Details
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mobileMainView === "details" && rightPanelView === "about"}
              className={
                mobileMainView === "details" && rightPanelView === "about"
                  ? "mobile-view-tab active"
                  : "mobile-view-tab"
              }
              onClick={() => {
                setMobileMainView("details");
                setRightPanelView("about");
              }}
            >
              About
            </button>
          </nav>
        ) : null}

        {isCompactLayout && mobileMainView === "summary" ? (
          <MobileSummaryView
            rows={forecastTableRows}
            valueMode={forecastValueMode}
            selectedAdminRegion={selectedAdminRegion}
            selectedDistrict={region}
            speciesLabel={speciesLabel}
            startDate={startDate}
            endDate={endDate}
            generatedAt={epidemiaData?.generated_at}
            onSelectDistrict={handleSummaryDistrictSelect}
          />
        ) : (
        <section className="dashboard-grid fade-in-up delay-2">
          {/* Map */}
          {(!isCompactLayout || mobileMainView === "map") && (
          <div className="glass-card map-panel" data-tour="map-panel">
            <div className="map-panel-controls">
              <CurrentSituationSummary
                rows={forecastTableRows}
                dataLoading={showDataLoadingBanner}
                selectedAdminRegion={selectedAdminRegion}
                selectedDistrict={region}
                speciesLabel={speciesLabel}
                valueMode={forecastValueMode}
                insight={selectedDecisionInsight}
                alert={situationAlert}
                weekAlerts={isHistoricalAlertWeek ? mapAlerts : null}
                alertAnimationWeek={isHistoricalAlertWeek ? alertAnimationWeek : null}
                weekObservedPoint={situationWeekObservedPoint}
                observedHistory={situationObservedHistory}
                forecastPoints={situationForecastPoints}
                population={
                  selectedDecisionInsight?.populationAtRisk ??
                  selectedAlert?.population_at_risk ??
                  null
                }
                incidentRate={
                  region !== "All Regions"
                    ? incidentRateData?.[region] ??
                      incidentRateData?.[normalizeDistrictKey(region)] ??
                      null
                    : null
                }
                startDate={startDate}
                endDate={endDate}
                forecastHorizonWeeks={forecastWeeks}
              />

              <div data-tour="decision-layers">
              <DecisionLayers
                showEarlyWarning={showEarlyWarning}
                onToggleEarlyWarning={() => setShowEarlyWarning(!showEarlyWarning)}
                showEarlyDetection={showEarlyDetection}
                onToggleEarlyDetection={() => setShowEarlyDetection(!showEarlyDetection)}
                alertTimeMode={alertTimeMode}
                onChangeAlertTimeMode={setAlertTimeMode}
                alertWeekDates={alertWeekDates}
                alertWeekIndex={alertWeekIndex}
                onChangeAlertWeekIndex={setAlertWeekIndex}
                alertPlaying={alertPlaying}
                onToggleAlertPlaying={() => setAlertPlaying((v) => !v)}
                alertHistoryWeeks={alertHistoryWeeks}
                onChangeAlertHistoryWeeks={setAlertHistoryWeeks}
                alertWeekCounts={alertWeekCounts}
              />
              </div>

              <div data-tour="env-layers">
              <EnvironmentalLayers
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={handleStartDateChange}
                onChangeEndDate={handleEndDateChange}
                availableWeeks={availableEpiweeks}
                mapSurfaceLayer={activeMapSurfaceLayer}
                onChangeMapSurfaceLayer={handleMapSurfaceLayerChange}
                showEnvTimeControls={envMapLayerActive}
                timeMode={envTimeMode}
                onChangeTimeMode={setEnvTimeMode}
                weekDates={weekDates}
                weekIndex={weekIndex}
                onChangeWeekIndex={setWeekIndex}
                playing={envPlaying}
                onTogglePlaying={() => setEnvPlaying((v) => !v)}
                averageSampleInfo={averageSampleInfo}
              />
              </div>
            </div>

            <Suspense fallback={<div className="map-panel-skeleton" role="status">Loading map…</div>}>
              <EthiopiaMap
                onSelectRegion={handleMapDistrictSelect}
                startDate={startDate}
                endDate={endDate}
                dataset={mapDataset}
                envData={mapEnvData}
                populationYear={populationYear}
                setGeoData={setGeoData}
                woredaPcodeCrosswalk={woredaPcodeCrosswalk}
                filterRegion={mapFilterForView}
                alerts={mapAlerts}
                alertTooltipByDistrict={alertTooltipByDistrict}
                districtTooltipByDistrict={districtTooltipByDistrict}
                mapTooltipContext={mapTooltipContext}
                selectedSpecies={mapSpecies}
                showEarlyWarning={reportExportActive ? false : showEarlyWarning}
                showEarlyDetection={reportExportActive ? false : showEarlyDetection}
                alertTimeMode={alertTimeMode}
                alertAnimationWeek={alertAnimationWeek}
                selectedDistrictName={mapSelectedDistrict}
                showRainfallLayer={reportExportActive ? false : showRainfallLayer}
                showTemperatureLayer={reportExportActive ? false : showTemperatureLayer}
                showNdviLayer={reportExportActive ? false : showNdviLayer}
                envTimeMode={envTimeMode}
                envTimeDate={weekDates[weekIndex]}
                gibsPrefetchTime={gibsPrefetchTime}
                onEnvAverageStats={setEnvAverageStats}
                healthChoroplethEnabled={reportExportActive ? true : healthChoroplethEnabled}
              />
            </Suspense>

            {SHOW_FETCH_ENVIRONMENTAL_DATA_PANEL && (
              <div className="glass-card fade-in-up delay-2 env-fetch-sidebar">
                <EnvironmentalDataControls
                  geoData={geoData}
                  startDate={startDate}
                  endDate={endDate}
                  dataset={dataset}
                  setDataset={setDataset}
                  onDataFetched={setEnvData}
                />
              </div>
            )}
          </div>
          )}

          {/* Charts / table tabs */}
          {(!isCompactLayout || mobileMainView === "details") && (
          <div
            className={`glass-card insights-panel side-panel${
              rightPanelView === "charts" ? " insights-panel--evidence" : ""
            }`}
          >
            <div className="panel-header">
              <h3>
                {rightPanelView === "about" ? "About EPIDEMIA" : region}
              </h3>
              <span className="panel-header-meta">
                {rightPanelView === "about" ? (
                  "Project overview"
                ) : rightPanelView === "decision" ? (
                  <>
                    Confirm · override · annotate
                    <HelpTip text={DASHBOARD_HELP.decisionTab} label="Action tab" />
                  </>
                ) : rightPanelView === "table" ? (
                  <>
                    Case projections
                    <HelpTip text={DASHBOARD_HELP.tableTab} label="Forecast tab" />
                  </>
                ) : selectedAlert ? (
                  `Alert · population at risk: ${formatPopulation(selectedAlert.population_at_risk)}`
                ) : rightPanelView === "charts" ? null : (
                  <>
                    Alert evidence &amp; charts
                    <HelpTip text={DASHBOARD_HELP.districtInsight} label="Evidence tab" />
                  </>
                )}
              </span>
            </div>

            {/* Workflow breadcrumb — shows the intended sequence */}
            <div className="workflow-breadcrumb" aria-label="Decision workflow">
              <span className={`workflow-step${rightPanelView === "charts" ? " workflow-step--active" : ""}`}>
                <span className="workflow-step-num">3</span> Evidence
              </span>
              <span className="workflow-step-arrow">›</span>
              <span className={`workflow-step${rightPanelView === "table" ? " workflow-step--active" : ""}`}>
                <span className="workflow-step-num">4</span> Forecast
              </span>
              {ediEnabled && (
                <>
                  <span className="workflow-step-arrow">›</span>
                  <span className={`workflow-step${rightPanelView === "decision" ? " workflow-step--active" : ""}`}>
                    <span className="workflow-step-num">5</span> Action
                  </span>
                </>
              )}
            </div>

            <div className="side-panel-tabs" role="tablist" aria-label="Insights views">
              <button
                type="button"
                role="tab"
                aria-selected={rightPanelView === "charts"}
                className={rightPanelView === "charts" ? "side-panel-tab active" : "side-panel-tab"}
                onClick={() => setRightPanelView("charts")}
                data-tour="tab-charts"
              >
                <span className="side-panel-tab-label">
                  Evidence
                  <HelpTip text={DASHBOARD_HELP.chartsTab} label="Evidence tab" />
                </span>
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={rightPanelView === "table"}
                className={rightPanelView === "table" ? "side-panel-tab active" : "side-panel-tab"}
                onClick={() => setRightPanelView("table")}
                data-tour="tab-table"
              >
                <span className="side-panel-tab-label">
                  Forecast
                  <HelpTip text={DASHBOARD_HELP.tableTab} label="Forecast tab" />
                </span>
              </button>
              {ediEnabled ? (
                <button
                  type="button"
                  role="tab"
                  aria-selected={rightPanelView === "decision"}
                  className={
                    rightPanelView === "decision" ? "side-panel-tab active" : "side-panel-tab"
                  }
                  onClick={() => setRightPanelView("decision")}
                  data-tour="tab-decision"
                >
                  <span className="side-panel-tab-label">
                    Action
                    <HelpTip text={DASHBOARD_HELP.decisionTab} label="Action tab" />
                  </span>
                </button>
              ) : null}
              <button
                type="button"
                role="tab"
                aria-selected={rightPanelView === "about"}
                className={rightPanelView === "about" ? "side-panel-tab active" : "side-panel-tab"}
                onClick={() => setRightPanelView("about")}
              >
                <span className="side-panel-tab-label">
                  About
                  <HelpTip text={DASHBOARD_HELP.aboutTab} label="About tab" />
                </span>
              </button>
            </div>

            {rightPanelView === "charts" && (
              <div className="side-panel-body charts-view" role="tabpanel">
                {epidemiaError && (
                  <div className="chart-state chart-state-error">{epidemiaError}</div>
                )}

                {epidemiaLoading && (
                  <div className="chart-state">Loading latest district forecast...</div>
                )}

                {epidemiaRefreshing && (
                  <div className="chart-state">
                    {pipelineStatus.detail || "Updating district forecast…"}
                  </div>
                )}

                <Suspense fallback={<div className="chart-state">Loading charts...</div>}>
                  <RegionalAlertSummaryChart
                    rows={forecastTableRows}
                    selectedAdminRegion={selectedAdminRegion}
                    speciesLabel={speciesLabel}
                    compact={isCompactLayout}
                    inChartsPanel
                  />

                  {selectedForecast && (
                    <section className="forecast-panel" data-tour="forecast-panel">
                      <div className="forecast-panel-header">
                        <div className="forecast-panel-toolbar">
                          <h4 className="forecast-panel-title">
                            <span className="forecast-panel-title-text">
                              Forecast ({selectedSpecies.toUpperCase()})
                            </span>
                            {seasonalContext ? (
                              <SeasonalContextRing context={seasonalContext} />
                            ) : null}
                            <AlertStatusIcons
                              earlyWarning={Boolean(selectedAlert?.early_warning)}
                              earlyDetection={Boolean(selectedAlert?.early_detection)}
                            />
                          </h4>
                          <label className="forecast-panel-horizon-control">
                            <SituationStatCircle
                              kind="pipeline"
                              label="Pipeline"
                              helpText={DASHBOARD_HELP.pipeline}
                              helpLabel="Pipeline status"
                              value={pipelineStatus.label}
                              pipelineKind={pipelineStatus.kind}
                              detail={pipelineStatus.detail}
                              progressPercent={pipelineStatus.progress?.percent}
                              showLabel={false}
                              showCaption={false}
                              compact
                            />
                            <select
                              className="toolbar-select"
                              value={forecastWeeks}
                              onChange={(e) => setForecastWeeks(Number(e.target.value))}
                              aria-label="Forecast horizon in weeks"
                              title={DASHBOARD_HELP.forecastWeeks}
                            >
                              <option value={4}>4 wk</option>
                              <option value={8}>8 wk</option>
                              <option value={12}>12 wk</option>
                              <option value={20}>20 wk</option>
                              <option value={26}>26 wk</option>
                            </select>
                            <HelpTip
                              text={DASHBOARD_HELP.forecastWeeks}
                              label="Forecast horizon"
                            />
                          </label>
                          <label className="forecast-panel-value-mode-control">
                            <select
                              className="toolbar-select"
                              value={forecastValueMode}
                              onChange={(e) => setForecastValueMode(e.target.value)}
                              aria-label="Chart value mode"
                              title={DASHBOARD_HELP.forecastValueMode}
                            >
                              {FORECAST_VALUE_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <HelpTip
                              text={DASHBOARD_HELP.forecastValueMode}
                              label="Chart value mode"
                            />
                          </label>
                          <button
                            type="button"
                            className="toolbar-button forecast-panel-refresh-btn"
                            onClick={refreshEpidemia}
                            disabled={epidemiaRefreshing}
                            title="Refresh district forecast"
                          >
                            {epidemiaRefreshing ? "…" : "Refresh"}
                          </button>
                        </div>
                      </div>
                      {districtDetailLoading && (
                        <p className="forecast-panel-loading-note">
                          Loading full history…
                        </p>
                      )}
                      {!districtDetailLoading && selectedDistrictNeedsDetail && (
                        <p className="forecast-panel-loading-note forecast-panel-loading-note--muted">
                          Showing recent history only. Start the forecast API on port 8000 or run
                          npm run sync:district-caches for the full chart range.
                        </p>
                      )}
                      <div id="epidemia-report-forecast-chart">
                        <ForecastChart
                          data={selectedForecast}
                          alert={selectedAlert}
                          valueMode={forecastValueMode}
                          startDate={startDate}
                          endDate={endDate}
                          onPlotReady={makePlotReadyHandler("forecast")}
                          onPlotPurge={makePlotPurgeHandler("forecast")}
                          registerHighlightResolver={registerHighlightResolver}
                          chartScopeKey={chartScopeKey}
                          districtKey={region !== "All Regions" ? region : ""}
                          syncedHoverDate={syncedHoverDate}
                          onHoverDateChange={setSyncedHoverDate}
                          alertTimeMode={alertTimeMode}
                          alertAnimationWeek={alertAnimationWeek}
                        />
                      </div>
                    </section>
                  )}

                  {!epidemiaLoading && !epidemiaRefreshing && !selectedForecast && region !== "All Regions" && (
                    <div className="chart-state">
                      No district forecast available for this selection.
                      {projectForecastDistricts.length > 0 &&
                        !projectForecastDistricts.includes(region) && (
                        <>
                          {" "}
                          This project includes: {projectForecastDistricts.join(", ")}.
                          {projectForecastDistricts.length === 1
                            ? " Select that district on the map."
                            : " Pick one of those districts on the map."}
                        </>
                      )}
                    </div>
                  )}

                  <section className="env-chart-panel" data-tour="env-chart-panel">
                    <div className="env-chart-panel-header">
                      <h4 className="env-chart-panel-title">
                        Weather time series
                        <HelpTip
                          text={DASHBOARD_HELP.weatherDataset}
                          label="Weather dataset"
                        />
                      </h4>
                      <label className="env-chart-dataset-control">
                        <span className="toolbar-field-label">Dataset</span>
                        <select
                          className="toolbar-select"
                          value={dataset}
                          onChange={(e) => setDataset(e.target.value)}
                          aria-label="Weather dataset for time series chart"
                        >
                          {WEATHER_DATASET_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                    <div id="epidemia-report-env-chart">
                      <EnvironmentalTimeSeriesChart
                        selectedDistrict={region !== "All Regions" ? region : null}
                        districtGeometry={selectedGeometry}
                        startDate={startDate}
                        endDate={endDate}
                        dataset={dataset}
                        onPlotReady={makePlotReadyHandler("env")}
                        onPlotPurge={makePlotPurgeHandler("env")}
                        registerHighlightResolver={registerHighlightResolver}
                        chartScopeKey={chartScopeKey}
                        syncedHoverDate={syncedHoverDate}
                        onHoverDateChange={setSyncedHoverDate}
                        alertTimeMode={alertTimeMode}
                        alertAnimationWeek={alertAnimationWeek}
                      />
                    </div>
                  </section>
                </Suspense>
              </div>
            )}

            {rightPanelView === "table" && (
              <div className="side-panel-body table-view" role="tabpanel">
                <ForecastAlertsTable
                  embedded
                  rows={forecastTableRows}
                  valueMode={forecastValueMode}
                  selectedDistrict={region}
                  comparisonDistricts={comparisonDistricts}
                  onToggleComparisonDistrict={toggleComparisonDistrict}
                />

                {!epidemiaLoading && !epidemiaRefreshing && (
                  <div className="table-view-comparison">
                    <div className="table-view-comparison-intro">
                      <div className="table-view-comparison-intro-header">
                        <h4>District comparison</h4>
                        <label className="comparison-highlight-mode">
                          <span>Highlight by</span>
                          <select
                            className="toolbar-select"
                            value={comparisonHighlightMode}
                            onChange={(event) => setComparisonHighlightMode(event.target.value)}
                          >
                            {comparisonHighlightModeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      </div>
                      <p>
                        When a region is selected, other districts in that region appear as grey
                        background lines; nationally, the top 10 priority districts are shown.
                        Use the selector above to compare highlight strategies. Click table rows or
                        grey chart lines to pick up to 3 districts manually. Solid lines = observed,
                        dotted = forecast.
                      </p>
                    </div>
                    <MultiDistrictComparisonChart
                      series={comparisonSeries}
                      backgroundSeries={comparisonBackgroundSeries}
                      valueMode={forecastValueMode}
                      startDate={startDate}
                      endDate={endDate}
                      chartScopeKey={chartScopeKey}
                      height={300}
                      syncedHoverDate={syncedHoverDate}
                      onHoverDateChange={setSyncedHoverDate}
                      onPlotReady={makePlotReadyHandler("compare")}
                      onPlotPurge={makePlotPurgeHandler("compare")}
                      registerHighlightResolver={registerHighlightResolver}
                      alertTimeMode={alertTimeMode}
                      alertAnimationWeek={alertAnimationWeek}
                      onSelectDistrict={toggleComparisonDistrict}
                    />
                    {ediEnabled ? (
                      <ComparisonPriorityPanel
                        candidates={comparisonPriorityCandidates}
                        species={selectedSpecies}
                        speciesLabel={speciesLabel}
                      />
                    ) : null}
                  </div>
                )}
              </div>
            )}

            {ediEnabled && rightPanelView === "decision" && (
              <div className="side-panel-body decision-view" role="tabpanel">
                <Suspense fallback={<div className="chart-state">Loading decision panel…</div>}>
                  <DecisionPanel
                  districtName={region !== "All Regions" ? region : null}
                  regionName={
                    selectedDecisionInsight?.region ||
                    resolveAdminRegionForDistrict(adm3Lookup, region) ||
                    selectedAdminRegion ||
                    ""
                  }
                  species={selectedSpecies}
                  speciesLabel={speciesLabel}
                  alert={selectedAlert}
                  insight={selectedDecisionInsight}
                  forecastPoints={selectedDecisionForecastPoints}
                  observedHistory={selectedDecisionObservedHistory}
                  reportGeneratedAt={epidemiaData?.generated_at || null}
                  neighborSummary={selectedDecisionNeighborSummary}
                  districtGeometry={selectedGeometry}
                  chartEndDate={endDate || null}
                  population={
                    selectedDecisionInsight?.populationAtRisk ??
                    selectedAlert?.population_at_risk ??
                    null
                  }
                  incidentRate={
                    region !== "All Regions"
                      ? incidentRateData?.[region] ??
                        incidentRateData?.[normalizeDistrictKey(region)] ??
                        null
                      : null
                  }
                />
                </Suspense>
              </div>
            )}

            {rightPanelView === "about" && (
              <div className="side-panel-body about-view" role="tabpanel">
                <AboutPanel />
              </div>
            )}
          </div>
          )}
        </section>
        )}
        </main>
      </div>

      <DashboardTour
        key={tourNonce}
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onPrepareStep={prepareTourStep}
        steps={tourSteps}
      />
    </div>
  );
}

export default Dashboard;
